import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Eye,
  MessageSquare,
  CreditCard,
  UserCheck,
  FileSearch,
  Lock,
  ChevronRight,
} from "lucide-react";

export default function AntiFraudPage({ user, onLogout, refreshUserData }) {
  const { t } = useLanguage();

  const warningSignals = [
    {
      icon: <CreditCard className="h-6 w-6" />,
      title: "Paiement suspect",
      description: "Demande de virement Interac, PayPal amis/famille, ou crypto uniquement. Refus des paiements sécurisés.",
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: "Pression à l'urgence",
      description: "'Dernier billet!', 'Offre expire dans 10 min', 'Plein d'acheteurs intéressés'. Tactiques de pression classiques.",
    },
    {
      icon: <UserCheck className="h-6 w-6" />,
      title: "Profil douteux",
      description: "Compte récent, pas de photo, aucun historique de vente, refuse de montrer une preuve d'identité.",
    },
    {
      icon: <Eye className="h-6 w-6" />,
      title: "Refus de montrer les billets",
      description: "Ne veut pas envoyer une capture d'écran ou refuse une rencontre en personne pour vérifier.",
    },
    {
      icon: <FileSearch className="h-6 w-6" />,
      title: "Prix trop beau",
      description: "Si le prix est 40-60% sous le marché, c'est probablement une arnaque. Méfiez-vous des 'urgences familiales'.",
    },
    {
      icon: <Lock className="h-6 w-6" />,
      title: "Communication hors plateforme",
      description: "Insiste pour continuer sur WhatsApp, Telegram ou par email personnel plutôt que sur la plateforme.",
    },
  ];

  const protectionSteps = [
    {
      number: "01",
      title: "Utilisez Middleman",
      description: "Notre service de tiers de confiance protège votre argent jusqu'à la confirmation de réception des billets.",
    },
    {
      number: "02",
      title: "Vérifiez l'identité",
      description: "Demandez une pièce d'identité et comparez avec le nom sur les billets. Faites un appel vidéo si possible.",
    },
    {
      number: "03",
      title: "Exigez des preuves",
      description: "Capture d'écran de la confirmation d'achat, du compte Ticketmaster connecté, des billets eux-mêmes.",
    },
    {
      number: "04",
      title: "Rencontrez en personne",
      description: "Si possible, faites l'échange en personne dans un lieu public. Vérifiez les billets avant de payer.",
    },
    {
      number: "05",
      title: "Utilisez un paiement sécurisé",
      description: "Privilégiez les paiements avec protection acheteur: carte de crédit via Middleman, PayPal Biens & Services.",
    },
    {
      number: "06",
      title: "Documentez tout",
      description: "Gardez toutes les conversations, captures d'écran, preuves de paiement. Utiles en cas de litige.",
    },
  ];

  const fraudTypes = [
    {
      type: "Billets fantômes",
      description: "Le vendeur n'a jamais eu les billets. Une fois payé, il disparaît.",
      prevalence: "Très fréquent",
    },
    {
      type: "Billets déjà vendus",
      description: "Les mêmes billets sont vendus à plusieurs acheteurs. Seul le premier à entrer pourra les utiliser.",
      prevalence: "Fréquent",
    },
    {
      type: "Faux billets",
      description: "Billets contrefaits ou captures d'écran modifiées. Non valides à l'entrée.",
      prevalence: "Modéré",
    },
    {
      type: "Transfert annulé",
      description: "Le vendeur transfère les billets puis les récupère après paiement via le support Ticketmaster.",
      prevalence: "Modéré",
    },
    {
      type: "Usurpation d'identité",
      description: "Le fraudeur se fait passer pour quelqu'un d'autre en utilisant de fausses preuves.",
      prevalence: "Occasionnel",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-700 to-brand-900 text-white py-20">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-20 w-20 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Guide Anti-Fraude
          </h1>
          <p className="text-xl text-brand-100 max-w-3xl mx-auto mb-8">
            Apprenez à reconnaître les arnaques et protégez-vous lors de vos achats
            de billets entre particuliers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/fraudes">
              <Button size="lg" variant="secondary" className="gap-2">
                <AlertTriangle className="h-5 w-5" />
                Voir les cas de fraudes
              </Button>
            </Link>
            <Link to="/auth">
              <Button size="lg" className="bg-white/10 hover:bg-white/20 border-white/30 gap-2">
                Créer une transaction sécurisée
                <ChevronRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Warning Signs Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              <AlertTriangle className="h-8 w-8 inline-block mr-3 text-red-500" />
              Signaux d'alarme
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Ces indices doivent vous alerter immédiatement. Si vous en repérez un ou plusieurs,
              prenez du recul et vérifiez soigneusement avant de procéder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {warningSignals.map((signal, index) => (
              <Card key={index} className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-red-100 rounded-lg text-red-600">
                      {signal.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-800 mb-2">{signal.title}</h3>
                      <p className="text-slate-600 text-sm">{signal.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Fraud */}
      <section className="py-16 bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Types de fraudes courants</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Connaître les techniques utilisées par les fraudeurs vous aide à les identifier.
            </p>
          </div>

          <div className="space-y-4">
            {fraudTypes.map((fraud, index) => (
              <div
                key={index}
                className="bg-slate-700/50 rounded-xl p-6 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <XCircle className="h-6 w-6 text-red-400" />
                    <h3 className="font-semibold text-lg">{fraud.type}</h3>
                  </div>
                  <p className="text-slate-300">{fraud.description}</p>
                </div>
                <div className="md:text-right">
                  <span className="inline-block px-3 py-1 bg-slate-600 rounded-full text-sm">
                    {fraud.prevalence}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protection Steps */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              <CheckCircle2 className="h-8 w-8 inline-block mr-3 text-brand-600" />
              Comment vous protéger
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Suivez ces étapes pour minimiser les risques lors de vos transactions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {protectionSteps.map((step, index) => (
              <div key={index} className="relative">
                <span className="absolute -top-4 -left-4 text-7xl font-bold text-brand-100 z-0">
                  {step.number}
                </span>
                <Card className="relative z-10 h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 pt-8">
                    <h3 className="font-semibold text-lg text-slate-800 mb-3">{step.title}</h3>
                    <p className="text-slate-600">{step.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-600 to-brand-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl font-bold mb-4">La solution: Middleman</h2>
          <p className="text-brand-100 text-lg mb-8 max-w-2xl mx-auto">
            Notre service de tiers de confiance retient le paiement jusqu'à ce que vous confirmiez
            avoir reçu les billets valides. Aucun risque pour l'acheteur.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" variant="secondary" className="gap-2">
                Créer un compte gratuit
                <ChevronRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                Comment ça marche?
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
